import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, AlertCircle, X, Plus, Package } from 'lucide-react';
import { toast } from 'sonner';
import { SKUGrid } from '../components/dispatch/SKUGrid';
import { DispatchHistory } from '../components/dispatch/DispatchHistory';
import { ChallanModal } from '../components/dispatch/ChallanModal';
import { EditChallanDialog } from '../components/dispatch/EditChallanDialog';
import { DeleteChallanDialog } from '../components/dispatch/DeleteChallanDialog';

const API = '';
const MAX_POS = 3;

export function DispatchNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role || 'supervisor';
  const canEditDelete = userRole === 'admin' || userRole === 'manager';

  // PO list
  const [pos, setPOs] = useState([]);
  const [buyers, setBuyers] = useState([]);

  // Multi-PO selection
  const [selectedPOIds, setSelectedPOIds] = useState([]);
  const [poSearchQuery, setPOSearchQuery] = useState('');
  const [showPODropdown, setShowPODropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowPODropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Merged SKUs from all selected POs (includes po_id + po_number per SKU)
  const [skus, setSKUs] = useState([]);

  // Form state
  const [dispatchDate, setDispatchDate] = useState(new Date());
  const [dispatchEntries, setDispatchEntries] = useState({});
  const [jobCardEntries, setJobCardEntries] = useState({});
  const [brandEntries, setBrandEntries] = useState({});
  const [rateEntries, setRateEntries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [transporter, setTransporter] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [totalCartons, setTotalCartons] = useState('');

  // Challan modal
  const [challanModal, setChallanModal] = useState(false);
  const [lastChallan, setLastChallan] = useState(null);

  // History
  const [dispatchHistory, setDispatchHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Edit/Delete
  const [editModal, setEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editChallan, setEditChallan] = useState(null);
  const [editForm, setEditForm] = useState({ vehicle_no: '', transporter: '', invoice_no: '', total_cartons: '', items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchPOs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/po?status=pending&limit=500`, { withCredentials: true });
      setPOs(data.pos || data);
    } catch { /* */ }
  }, []);

  const fetchBuyers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/buyers`, { withCredentials: true });
      setBuyers(data);
    } catch { /* */ }
  }, []);

  const fetchDispatchHistory = useCallback(async (filters = {}) => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.po_number) params.append('po_number', filters.po_number);
      if (filters.buyer) params.append('buyer', filters.buyer);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('page', filters.page || 1);
      params.append('limit', '25');
      const { data } = await axios.get(`${API}/api/challans?${params}`, { withCredentials: true });
      setDispatchHistory(data.challans || data);
      setHistoryTotalPages(data.pages || 1);
      setHistoryTotal(data.total || 0);
    } catch { /* */ } finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => {
    fetchPOs();
    fetchBuyers();
    fetchDispatchHistory();
  }, [fetchPOs, fetchBuyers, fetchDispatchHistory]);

  // Load SKUs whenever selected POs change — only fetch new POs, merge with existing
  const prevPOIdsRef = useRef([]);
  useEffect(() => {
    const loadSKUs = async () => {
      if (selectedPOIds.length === 0) { setSKUs([]); prevPOIdsRef.current = []; return; }

      const prevIds = prevPOIdsRef.current;
      const newIds = selectedPOIds.filter(id => !prevIds.includes(id));
      const removedIds = prevIds.filter(id => !selectedPOIds.includes(id));

      // Remove SKUs from removed POs
      if (removedIds.length > 0) {
        setSKUs(prev => prev.filter(s => !removedIds.includes(s.po_id)));
      }

      // Fetch only newly added POs
      if (newIds.length > 0) {
        try {
          const results = await Promise.all(
            newIds.map(poId =>
              axios.get(`${API}/api/po/skus/${poId}`, { withCredentials: true }).then(r => {
                const po = pos.find(p => p.id === poId);
                return (r.data || []).map(sku => ({
                  ...sku,
                  po_id: poId,
                  po_number: po?.po_number || ''
                }));
              })
            )
          );
          setSKUs(prev => [...prev.filter(s => selectedPOIds.includes(s.po_id)), ...results.flat()]);
        } catch { toast.error('Failed to load SKU data'); }
      }

      prevPOIdsRef.current = [...selectedPOIds];
    };
    loadSKUs();
  }, [selectedPOIds, pos]);

  // Refresh history when POs selected
  useEffect(() => {
    if (selectedPOIds.length === 1) {
      const po = pos.find(p => p.id === selectedPOIds[0]);
      if (po) fetchDispatchHistory({ po_number: po.po_number });
    } else {
      fetchDispatchHistory();
    }
  }, [selectedPOIds, pos, fetchDispatchHistory]);

  const addPO = (poId) => {
    if (selectedPOIds.includes(poId)) return;
    if (selectedPOIds.length >= MAX_POS) {
      toast.warning(`Maximum ${MAX_POS} POs allowed per challan`);
      return;
    }
    // Enforce same buyer for all POs in one challan
    const newPO = pos.find(p => p.id === poId);
    if (selectedPOIds.length > 0 && newPO) {
      const firstPO = pos.find(p => p.id === selectedPOIds[0]);
      if (firstPO && firstPO.buyer_name !== newPO.buyer_name) {
        toast.error(`All POs in a challan must be from the same buyer. Current: ${firstPO.buyer_name}`);
        return;
      }
    }
    setSelectedPOIds(prev => [...prev, poId]);
    setShowPODropdown(false);
    setPOSearchQuery('');
  };

  const removePO = (poId) => {
    // Clear entries for this PO's SKUs
    const removedKeys = skus
      .filter(s => s.po_id === poId)
      .map(s => `${s.po_id}|||${s.sku}|||${s.size}`);
    setDispatchEntries(de => { const n = { ...de }; removedKeys.forEach(k => delete n[k]); return n; });
    setJobCardEntries(je => { const n = { ...je }; removedKeys.forEach(k => delete n[k]); return n; });
    setBrandEntries(be => { const n = { ...be }; removedKeys.forEach(k => delete n[k]); return n; });
    setRateEntries(re => { const n = { ...re }; removedKeys.forEach(k => delete n[k]); return n; });
    setSelectedPOIds(prev => prev.filter(id => id !== poId));
  };

  const makeKey = (poId, sku, size) => `${poId}|||${sku}|||${size}`;

  const updateDispatchQty = (poId, sku, size, value) => {
    setDispatchEntries(prev => ({ ...prev, [makeKey(poId, sku, size)]: value }));
  };
  const updateJobCard = (poId, sku, size, value) => {
    setJobCardEntries(prev => ({ ...prev, [makeKey(poId, sku, size)]: value }));
  };
  const updateBrand = (poId, sku, size, value) => {
    setBrandEntries(prev => ({ ...prev, [makeKey(poId, sku, size)]: value }));
  };
  const updateRate = (poId, sku, size, value) => {
    setRateEntries(prev => ({ ...prev, [makeKey(poId, sku, size)]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const entries = Object.entries(dispatchEntries)
      .filter(([_, qty]) => parseInt(qty) > 0)
      .map(([key, qty]) => {
        const [poId, sku, size] = key.split('|||');
        return {
          po_id: poId, sku, size,
          brand: brandEntries[key] || '',
          dispatch_qty: parseInt(qty),
          unit: 'Pairs',
          rate: parseFloat(rateEntries[key]) || 0,
          job_card_number: jobCardEntries[key] || ''
        };
      });

    if (entries.length === 0) { setError('Please enter at least one dispatch quantity'); return; }

    for (const entry of entries) {
      if (!entry.job_card_number.trim()) {
        const po = pos.find(p => p.id === entry.po_id);
        setError(`Job Card Number is mandatory for ${entry.sku} Size ${entry.size} (PO ${po?.po_number || ''})`);
        return;
      }
    }

    for (const entry of entries) {
      const skuData = skus.find(s => s.po_id === entry.po_id && s.sku === entry.sku && s.size === entry.size);
      if (skuData && entry.dispatch_qty > skuData.balance_qty) {
        const po = pos.find(p => p.id === entry.po_id);
        setError(`Dispatch qty for ${entry.sku} Size ${entry.size} (PO ${po?.po_number || ''}) exceeds balance (${skuData.balance_qty})`);
        return;
      }
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/dispatch/batch`, {
        po_ids: selectedPOIds,
        dispatch_date: format(dispatchDate, 'yyyy-MM-dd'),
        vehicle_no: vehicleNo,
        transporter,
        invoice_no: invoiceNo,
        total_cartons: totalCartons ? parseInt(totalCartons) : null,
        items: entries
      }, { withCredentials: true });

      toast.success(`Dispatch created. Challan: ${data.challan_number}`);
      setLastChallan(data);
      setChallanModal(true);

      // Reset form entries but keep PO selection
      setDispatchEntries({});
      setJobCardEntries({});
      setBrandEntries({});
      setRateEntries({});
      setVehicleNo('');
      setTransporter('');
      setInvoiceNo('');
      setTotalCartons('');

      // Reload SKUs for selected POs after dispatch
      prevPOIdsRef.current = [];
      setSelectedPOIds(prev => [...prev]);
      fetchDispatchHistory();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create dispatch');
    } finally { setLoading(false); }
  };

  // ---- Edit Challan ----
  const openEditModal = async (challanNumber) => {
    try {
      const { data } = await axios.get(`${API}/api/challans/${challanNumber}`, { withCredentials: true });
      setEditChallan(data);
      // Older / single-PO challans store po_number only at top level, not on each item.
      const fallbackPO = (data.po_numbers && data.po_numbers[0]) || data.po_number || '';
      setEditForm({
        vehicle_no: data.vehicle_no || '',
        transporter: data.transporter || '',
        invoice_no: data.invoice_no || '',
        total_cartons: data.total_cartons ?? '',
        items: (data.items || []).map(it => ({
          po_number: it.po_number || fallbackPO, sku: it.sku, size: it.size, brand: it.brand || '',
          dispatch_qty: it.dispatch_qty, unit: it.unit || 'Pairs',
          rate: it.rate || 0, job_card_number: it.job_card_number || '',
        })),
      });
      setEditModal(true);
    } catch { toast.error('Failed to load challan details'); }
  };

  const handleEditItemChange = (idx, field, value) => {
    setEditForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: field === 'dispatch_qty' ? parseInt(value) || 0 : field === 'rate' ? parseFloat(value) || 0 : value };
      return { ...prev, items };
    });
  };

  const submitChallanEdit = async () => {
    setEditLoading(true);
    try {
      await axios.put(`${API}/api/challans/${editChallan.challan_number}`, {
        vehicle_no: editForm.vehicle_no,
        transporter: editForm.transporter,
        invoice_no: editForm.invoice_no,
        total_cartons: editForm.total_cartons ? parseInt(editForm.total_cartons) : null,
        items: editForm.items.map(it => ({
          po_number: it.po_number, sku: it.sku, size: it.size, brand: it.brand,
          dispatch_qty: it.dispatch_qty, unit: it.unit,
          rate: it.rate, job_card_number: it.job_card_number,
        })),
      }, { withCredentials: true });
      toast.success('Challan updated successfully');
      setEditModal(false);
      setEditChallan(null);
      fetchDispatchHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update challan'); }
    finally { setEditLoading(false); }
  };

  const openDeleteModal = (challan) => { setDeleteTarget(challan); setDeleteModal(true); };

  const confirmDeleteChallan = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/api/challans/${deleteTarget.challan_number}`, { withCredentials: true });
      toast.success(`Challan ${deleteTarget.challan_number} deleted. PO balance restored.`);
      setDeleteModal(false); setDeleteTarget(null);
      fetchDispatchHistory();
      await fetchPOs();
      if (selectedPOIds.length > 0) { prevPOIdsRef.current = []; setSelectedPOIds(prev => [...prev]); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete challan'); }
    finally { setDeleteLoading(false); }
  };

  // Filtered PO options for dropdown — same buyer only
  const selectedBuyer = selectedPOIds.length > 0 ? pos.find(p => p.id === selectedPOIds[0])?.buyer_name : null;
  const availablePOs = pos.filter(po =>
    !selectedPOIds.includes(po.id) &&
    (!selectedBuyer || po.buyer_name === selectedBuyer) &&
    (poSearchQuery === '' ||
      po.po_number.toLowerCase().includes(poSearchQuery.toLowerCase()) ||
      po.buyer_name.toLowerCase().includes(poSearchQuery.toLowerCase()))
  );

  const selectedPOData = selectedPOIds.map(id => pos.find(p => p.id === id)).filter(Boolean);

  return (
    <div className="p-6" data-testid="dispatch-new-page">
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Dispatch Entry</h1>
        <p className="text-sm text-zinc-500">Record dispatch quantities against purchase orders — up to {MAX_POS} POs per challan</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-6 mb-6">
        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="dispatch-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* PO Selection + Date Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 pb-6 border-b border-zinc-200">
          {/* Multi-PO Selector */}
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Select POs * <span className="text-zinc-400">({selectedPOIds.length}/{MAX_POS})</span>
            </Label>

            {/* Selected PO Chips */}
            <div className="flex flex-wrap gap-2 mt-2 mb-2 min-h-[36px]">
              {selectedPOData.map(po => (
                <div key={po.id} className="flex items-center gap-1.5 bg-zinc-100 border border-zinc-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid={`selected-po-${po.po_number}`}>
                  <Package size={12} className="text-zinc-500" />
                  <span className="font-mono font-medium text-[#09090B]">{po.po_number}</span>
                  <span className="text-zinc-400">—</span>
                  <span className="text-zinc-600 text-xs">{po.buyer_name}</span>
                  <button type="button" onClick={() => removePO(po.id)} className="ml-1 hover:bg-zinc-200 rounded p-0.5" data-testid={`remove-po-${po.po_number}`}>
                    <X size={14} className="text-zinc-500" />
                  </button>
                </div>
              ))}
              {selectedPOIds.length === 0 && (
                <span className="text-sm text-zinc-400 py-1">No POs selected</span>
              )}
            </div>

            {/* Search/Add PO */}
            {selectedPOIds.length < MAX_POS && (
              <div className="relative" ref={dropdownRef}>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={poSearchQuery}
                      onChange={(e) => { setPOSearchQuery(e.target.value); setShowPODropdown(true); }}
                      onFocus={() => setShowPODropdown(true)}
                      placeholder="Search PO number or buyer..."
                      className="h-9 rounded-sm text-sm pr-8"
                      data-testid="po-search-input"
                    />
                    <Plus size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                  </div>
                </div>
                {showPODropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-48 overflow-y-auto" data-testid="po-dropdown">
                    {availablePOs.length === 0 ? (
                      <div className="p-3 text-sm text-zinc-500 text-center">No matching POs</div>
                    ) : (
                      availablePOs.slice(0, 20).map(po => (
                        <button
                          key={po.id}
                          type="button"
                          onClick={() => addPO(po.id)}
                          className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-0 flex justify-between items-center"
                          data-testid={`po-option-${po.po_number}`}
                        >
                          <div>
                            <span className="font-mono font-medium text-sm">{po.po_number}</span>
                            <span className="text-zinc-500 ml-2 text-xs">— {po.buyer_name}</span>
                          </div>
                          <span className="text-xs text-zinc-400">Bal: {po.total_balance?.toLocaleString()}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Dispatch Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full mt-1 h-9 rounded-sm justify-start text-left font-normal" data-testid="dispatch-date-trigger">
                  <CalendarIcon size={14} className="mr-2" />
                  {format(dispatchDate, 'yyyy-MM-dd')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dispatchDate} onSelect={(date) => date && setDispatchDate(date)} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Transport Details */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 pb-6 border-b border-zinc-200">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Vehicle No.</Label>
            <Input value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="MH-01-AB-1234" className="mt-1 h-8 rounded-sm text-sm" data-testid="vehicle-no" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Transporter</Label>
            <Input value={transporter} onChange={(e) => setTransporter(e.target.value)} placeholder="XYZ Transport" className="mt-1 h-8 rounded-sm text-sm" data-testid="transporter" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Invoice No.</Label>
            <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="INV-001" className="mt-1 h-8 rounded-sm text-sm" data-testid="invoice-no" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total Cartons</Label>
            <Input type="number" value={totalCartons} onChange={(e) => setTotalCartons(e.target.value)} placeholder="0" className="mt-1 h-8 rounded-sm text-sm" data-testid="total-cartons" />
          </div>
        </div>

        {/* PO Summary Chips */}
        {selectedPOData.length > 0 && (
          <div className="mb-4 space-y-2">
            {selectedPOData.map(po => (
              <div key={po.id} className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">PO:</span>
                    <span className="ml-2 font-mono font-medium">{po.po_number}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Buyer:</span>
                    <span className="ml-2 font-medium">{po.buyer_name}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Order Date:</span>
                    <span className="ml-2 font-mono">{po.order_date}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Balance:</span>
                    <span className="ml-2 font-mono font-medium">{po.total_balance?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SKU Grid */}
        {selectedPOIds.length > 0 && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">Enter Dispatch Details</Label>
            {skus.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">Loading SKU data...</div>
            ) : (
              <SKUGrid
                skus={skus}
                dispatchEntries={dispatchEntries}
                jobCardEntries={jobCardEntries}
                brandEntries={brandEntries}
                rateEntries={rateEntries}
                updateDispatchQty={updateDispatchQty}
                updateJobCard={updateJobCard}
                updateBrand={updateBrand}
                updateRate={updateRate}
                showPOColumn={true}
              />
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-zinc-200">
          <Button type="button" variant="outline" onClick={() => navigate('/po/list')} className="h-9 rounded-sm">Cancel</Button>
          <Button type="submit" className="h-9 rounded-sm bg-[#09090B] hover:bg-[#27272A]" disabled={loading || selectedPOIds.length === 0} data-testid="submit-dispatch">
            {loading ? 'Processing...' : 'Create Dispatch & Generate Challan'}
          </Button>
        </div>
      </form>

      {/* DISPATCH HISTORY */}
      <DispatchHistory
        dispatchHistory={dispatchHistory} historyLoading={historyLoading}
        historyTotal={historyTotal} historyPage={historyPage}
        historyTotalPages={historyTotalPages} setHistoryPage={setHistoryPage}
        fetchDispatchHistory={fetchDispatchHistory} selectedPOData={selectedPOData.length === 1 ? selectedPOData[0] : null}
        canEditDelete={canEditDelete} buyers={buyers}
        openEditModal={openEditModal} openDeleteModal={openDeleteModal}
      />

      <ChallanModal open={challanModal} onOpenChange={(open) => { setChallanModal(open); if (!open) setLastChallan(null); }} challan={lastChallan} />
      <EditChallanDialog open={editModal} onOpenChange={setEditModal} editChallan={editChallan} editForm={editForm} setEditForm={setEditForm} handleEditItemChange={handleEditItemChange} submitChallanEdit={submitChallanEdit} editLoading={editLoading} />
      <DeleteChallanDialog open={deleteModal} onOpenChange={setDeleteModal} deleteTarget={deleteTarget} confirmDelete={confirmDeleteChallan} deleteLoading={deleteLoading} />
    </div>
  );
}
